const MAX_MY_LIST_ANCHORS = 4;
const MAX_STARTER_ANCHORS = 6;

const PREFERRED_STARTER_SLUGS = [
  "ted-lasso-2020",
  "only-murders-in-the-building-2021",
  "shrinking-2023",
  "x-men-97-2024",
];

function hashSeed(value) {
  let hash = 0;
  for (const character of String(value || "")) {
    hash = ((hash << 5) - hash + character.charCodeAt(0)) | 0;
  }
  return Math.abs(hash);
}

function getWeeklySeed(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const daysSinceMonday = (date.getUTCDay() + 6) % 7;
  const monday = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() - daysSinceMonday));
  return monday.toISOString().slice(0, 10);
}

function rotate(items, offset) {
  if (!items.length) return [];
  const start = offset % items.length;
  return [...items.slice(start), ...items.slice(0, start)];
}

function normalizeSlug(value) {
  return typeof value === "string" ? value.trim() : "";
}

export function getRecommendationDestination(show) {
  const slug = normalizeSlug(show?.slug);
  return show?.hasShowsLikePage === true && slug
    ? `/shows-like/${slug}`
    : null;
}

export function isRecommendationAnchor(show) {
  return Boolean(getRecommendationDestination(show) && String(show?.title || "").trim());
}

export function getRecommendationSuggestionMeta(show) {
  const releaseYear = typeof show?.releaseDate === "string"
    ? /^(\d{4})-/.exec(show.releaseDate)?.[1]
    : null;
  const yearMonthYear = typeof show?.yearMonth === "string"
    ? /^(\d{4})-/.exec(show.yearMonth)?.[1]
    : null;
  const slugYear = /-(\d{4})$/.exec(normalizeSlug(show?.slug))?.[1] || "";
  const year = show?.year ? String(show.year) : slugYear || releaseYear || yearMonthYear;
  const sourceCandidate = show?.source || show?.sourceName || show?.networks?.[0];
  const source = typeof sourceCandidate === "string" ? sourceCandidate.trim() : "";

  return [year, source].filter(Boolean).join(" · ");
}

export function getEligibleRecommendationShows(shows = []) {
  return Array.isArray(shows)
    ? shows.filter(isRecommendationAnchor)
    : [];
}

export function mergeRecommendationCatalog(primaryShows = [], supplementalShows = []) {
  const bySlug = new Map();
  for (const show of [...primaryShows, ...supplementalShows]) {
    const slug = normalizeSlug(show?.slug);
    if (!slug) continue;
    const existing = bySlug.get(slug);
    bySlug.set(slug, existing ? { ...existing, ...show } : show);
  }
  return [...bySlug.values()];
}

export function selectRecommendationStarters(shows = [], limit = MAX_STARTER_ANCHORS, referenceDate = null) {
  const eligible = getEligibleRecommendationShows(shows)
    .filter((show) => show?.poster || show?.posterMetadata?.thumbnail);
  const preference = new Map(PREFERRED_STARTER_SLUGS.map((slug, index) => [slug, index]));
  const preferred = eligible
    .filter((show) => preference.has(show.slug))
    .sort((left, right) => preference.get(left.slug) - preference.get(right.slug));
  const remaining = eligible.filter((show) => !preference.has(show.slug));
  const seed = hashSeed(getWeeklySeed(referenceDate));
  const familiarCount = Math.min(3, preferred.length, limit);
  const familiar = rotate(preferred, seed).slice(0, familiarCount);
  const others = rotate(remaining, seed * 7 + 3).slice(0, Math.max(0, limit - familiar.length));
  return [...familiar, ...others];
}

export function resolveMyListRecommendationAnchors(entries = [], showsBySlug, limit = MAX_MY_LIST_ANCHORS) {
  if (!Array.isArray(entries) || !showsBySlug?.get) return [];

  const anchors = [];
  const seen = new Set();
  for (const entry of entries) {
    const slug = normalizeSlug(entry?.slug);
    if (!slug || seen.has(slug)) continue;
    const show = showsBySlug.get(slug);
    if (!isRecommendationAnchor(show)) continue;
    seen.add(slug);
    anchors.push(show);
    if (anchors.length >= limit) break;
  }
  return anchors;
}
